/**
 * The browser's `Actor` — decision 0005, contract C-19 and C-29.
 *
 * The menu hides what a role cannot reach, and the frontend decides that with **the same
 * `can()` the API enforces with**, imported from `@tvitvi/shared`. Not with a
 * `permissions: string[]` answer computed by the API: C-29 rejected that precisely because it
 * would be a fourth encoding of authorization, free to drift from `RULES`, `role_permissions`
 * and RLS.
 *
 * `/v1/me` hands the client the flat `Action[]` for its own role. This file turns it back into
 * an `Actor` — one function, so no component ever assembles one itself.
 *
 * WHAT IS DELIBERATELY EMPTY. `editionRoles` and `grants` are Phase 2 data and are not on
 * `/v1/me` yet (`departmentIds` is, since QA6 K2 #2). That makes the browser's answer
 * *narrower* than the server's for a `limited` or `external` actor, never wider: a `dept`-scoped list still renders, the server
 * still narrows the rows, and no button appears that the API would refuse. Deny by default
 * survives an incomplete actor; allow by default would not.
 */
import {
  can,
  canReach,
  permissionSetFromActions,
  splitAction,
  type Action,
  type Actor,
  type CollectionResource,
  type MeResponse,
  type OrganizationId,
  type OrganizationModule,
  type PersonId,
  type Resource,
  type ResourceType,
  type UserId,
  type Uuid,
} from '@tvitvi/shared';

import { useMe } from '@/api/queries/me';
import { guestScopeFor } from './guest-org';
import { organizationKindOf } from './organization-kind';
import { declaredModulesOf } from './organization-modules';

/** `null` when the person has no active organization — onboarding, not a denial. */
export function actorFromMe(me: MeResponse): Actor | null {
  const organizationId = me.activeOrganizationId as OrganizationId | null;
  const orgRole = me.activeOrgRole;
  if (organizationId === null || orgRole === null) return null;
  /* Modulene (239.40): provisjon og avtaler følger modulen Byrå. Uten feltet gjelder typen. */
  const modules = declaredModulesOf(me);

  return {
    userId: me.user.id as UserId,
    personId: me.person.id as PersonId,
    organizationId,
    /*
     * HVA SLAGS HUS (QA14 #3a). Den ene av aktørens fire dimensjoner som IKKE er tom her.
     *
     * `/v1/me` bærer den allerede på medlemskapet — menyen har lest den siden vedtak 93 — så
     * nettleseren kan svare det samme som serveren på `artist_agreement:*` uten et kall til.
     * `organizationKindOf` faller tilbake på `festival`, som er den lukkende retningen: regelen
     * slipper bare `agency` gjennom.
     */
    organizationKind: organizationKindOf(me),
    ...(modules === undefined ? {} : { modules: new Set(modules) }),
    orgRole,
    editionRoles: new Map(),
    /*
     * Tom her av den samme grunnen som de andre tre, og med den samme retningen på feilen: en
     * rolle uten `festival:access_all` får `{ kind: 'assigned', festivalIds: [] }` i nettleseren,
     * altså INGEN festivaler — smalere enn serverens svar, aldri videre. Sidene henter uansett
     * radene fra API-et, som har den ekte lista.
     */
    festivalAccess: new Set(),
    /*
     * «EGEN AVDELING» (QA6 K2 #2). Settet `resolve_actor` ga serveren. Tomt her gjorde hver
     * `dept`-celle til nei i nettleseren mens API-et sa ja, og Begrenset fikk aldri en knapp.
     */
    departmentIds: new Set((me.departmentIds ?? []) as Uuid[]),
    grants: [],
    permissions: permissionSetFromActions(organizationId, orgRole, me.permissions),
    /*
     * GJESTENS DELINGER (sak 232, T5), slik `GET /v1/me/guest-scope` svarte da rutevakten lastet
     * (`loadMe`). Uten dem svarer `guestCan` nei på hver post, og «kan endre» ville ikke vist en
     * eneste knapp. Et medlem har ingen.
     */
    guest: orgRole === 'guest' ? guestScopeFor(organizationId) : null,
  };
}

/** The whole collection of one resource type, in the actor's own organization. */
export function collectionFor(actor: Actor, type: ResourceType): CollectionResource {
  return { kind: 'collection', type, organizationId: actor.organizationId };
}

/**
 * "May this person do this?" — the question a menu entry, a button and a route guard all ask.
 * With no resource it asks about the collection the action names (`contact:list` → contacts).
 */
export function allows(actor: Actor | null, action: Action, resource?: Resource): boolean {
  if (actor === null) return false;
  const target = resource ?? collectionFor(actor, splitAction(action).resource);
  return can(actor, action, target).allowed;
}

/**
 * «Kan rollen nå i det minste én rad av dette?» — spørsmålet en RUTE stiller før den vet hvilken rad
 * den skal vise (QA runde 5, H5/H7). `allows` om samlingen sier nei for hver celle med en
 * radbetingelse (egen oppgave, delt programpost); API-et avgjør raden. Se `canReach`.
 */
export function reaches(actor: Actor | null, action: Action): boolean {
  if (actor === null) return false;
  return canReach(actor, action, collectionFor(actor, splitAction(action).resource));
}

/**
 * DET SAMME SPØRSMÅLET, STILT INNE I ÉN MODUL (G1, QA13). Menypunktet Spillesteder og seksjonen
 * Spillested spør begge `venue:list`, men om hver sin modul; `can()` krever at organisasjonen har
 * akkurat den (`moduleGate` i shared). Uten modul er det `allows`/`reaches`.
 */
export function allowsIn(
  actor: Actor | null,
  action: Action,
  module: OrganizationModule | undefined,
): boolean {
  if (actor === null) return false;
  if (module === undefined) return allows(actor, action);
  return can(actor, action, {
    ...collectionFor(actor, splitAction(action).resource),
    inModule: module,
  }).allowed;
}

export function reachesIn(
  actor: Actor | null,
  action: Action,
  module: OrganizationModule | undefined,
): boolean {
  if (actor === null) return false;
  if (module === undefined) return reaches(actor, action);
  return canReach(actor, action, {
    ...collectionFor(actor, splitAction(action).resource),
    inModule: module,
  });
}

/** The signed-in actor, from the `me` the shell already loaded. Never a second request. */
export function useActor(): Actor | null {
  return actorFromMe(useMe());
}
